// src/pages/HomePage.js
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { makeImageAsset } from '../utils/makeImageAsset';
import buildingExterior4890 from '../assets/reading-towers/building/gallery/exterior-dsc4890-1400.jpg';
import buildingExterior4890Full from '../assets/reading-towers/building/gallery/exterior-dsc4890-2800.jpg';
import buildingExterior4870 from '../assets/reading-towers/building/gallery/exterior-dsc4870-1400.jpg';
import buildingExterior4870Full from '../assets/reading-towers/building/gallery/exterior-dsc4870-2800.jpg';
import x17Living from '../assets/reading-towers/units/reference/rt-x17/x17-living-1536.jpg';

const imageSources = {
  exterior: makeImageAsset({
    src: buildingExterior4890,
    src2x: buildingExterior4890Full,
    alt: 'Reading Tower exterior at 601 Penn Street',
  }),
  residence: makeImageAsset({
    src: x17Living,
    alt: 'Reading Tower living room',
  }),
  view: makeImageAsset({
    src: buildingExterior4870,
    src2x: buildingExterior4870Full,
    alt: 'Reading Tower exterior view',
  }),
};

const slidesData = [
  {
    id: 1,
    imageKey: 'exterior',
    title: 'Welcome to Reading Tower Apartments',
    subtitle: 'Rooted in Downtown, Reimagined for Comfortable Living',
    titleFontFamily: "'Roboto', sans-serif",
    titleFontWeightClass: "font-medium",
    subtitleFontFamily: "'Roboto', sans-serif",
    subtitleFontWeightClass: "font-light",
  },
  {
    id: 2,
    imageKey: 'residence',
    title: 'Modern Downtown Residences',
    subtitle: 'Studio, One Bedroom, and Two Bedroom Apartments at 601 Penn Street',
    titleFontFamily: "'Roboto', sans-serif", titleFontWeightClass: "font-medium", subtitleFontFamily: "'Roboto', sans-serif", subtitleFontWeightClass: "font-light",
  },
  {
    id: 3,
    imageKey: 'view',
    title: 'Views Across Reading',
    subtitle: 'Comfortable Living in a Central Downtown Location',
    titleFontFamily: "'Roboto', sans-serif", titleFontWeightClass: "font-medium", subtitleFontFamily: "'Roboto', sans-serif", subtitleFontWeightClass: "font-light",
  },
];

const HomePage = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === slidesData.length - 1 ? 0 : prevIndex + 1
    );
  }, []);

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? slidesData.length - 1 : prevIndex - 1
    );
  };

  const goToSlide = (slideIndex) => {
    setCurrentIndex(slideIndex);
  };

  useEffect(() => {
    const timer = setTimeout(nextSlide, 7000);
    return () => clearTimeout(timer);
  }, [currentIndex, nextSlide]);

  const overlayVariants = {
    hidden: { opacity: 0, y: -50, scaleX: 0.7, scaleY: 0.6 },
    visible: {
      opacity: 1,
      y: 0,
      scaleX: 1,
      scaleY: 1,
      transition: { duration: 0.7, ease: [0.33, 1, 0.68, 1] },
    },
  };

  const textVariants = {
    hidden: { opacity: 0, y: -20, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.6, ease: "easeOut", delay: 0.35 },
    },
  };

  const currentSlide = slidesData[currentIndex];

  return (
    <main>
      <section className="relative h-[calc(90vh-5rem)] w-full group overflow-hidden select-none">
        <div
          className="w-full h-full flex transition-transform ease-in-out duration-700"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {slidesData.map((slide, slideIndex) => (
            <div
              key={slide.id}
              className="w-full h-full flex-shrink-0 relative"
            >
              <div
                className={`w-full h-full bg-cover bg-center 
                           ${slideIndex === currentIndex ? 'kenburns-active-image' : ''}`}
                style={{
                  backgroundImage: `image-set(url(${imageSources[slide.imageKey].src}) 1x, url(${imageSources[slide.imageKey].src2x}) 2x)`,
                }}
              />
              <div className="absolute inset-0 bg-black opacity-40"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4">
                {slideIndex === currentIndex && (
                  <motion.div
                    key={currentIndex + "-overlay"}
                    className="p-6 sm:p-8 md:p-10 rounded-lg inline-block max-w-5xl"
                    variants={overlayVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <motion.div
                      key={currentIndex + "-text"}
                      variants={textVariants}
                      initial="hidden"
                      animate="visible"
                    >
                      <h1
                        className={`text-4xl sm:text-5xl md:text-6xl mb-3 sm:mb-4 leading-tight text-shadow-md text-white drop-shadow-md uppercase ${currentSlide.titleFontWeightClass}`}
                        style={{ fontFamily: currentSlide.titleFontFamily }}
                      >
                        {slide.title}
                      </h1>
                      <p
                        className={`text-xl sm:text-2xl md:text-3xl text-white text-shadow-md drop-shadow-sm uppercase ${currentSlide.subtitleFontWeightClass}`}
                        style={{ fontFamily: currentSlide.subtitleFontFamily }}
                      >
                        {slide.subtitle}
                      </p>
                    </motion.div>
                  </motion.div>
                )}
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={prevSlide}
          className="absolute top-1/2 left-5 sm:left-10 transform -translate-y-1/2 bg-black bg-opacity-30 hover:bg-opacity-50 text-white w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300 z-10 ring-1 ring-white/0 group-hover:ring-white/50 hover:ring-white/75 focus:ring-white/75"
          aria-label="Previous slide"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5 sm:w-6 sm:h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <button
          onClick={nextSlide}
          className="absolute top-1/2 right-5 sm:right-10 transform -translate-y-1/2 bg-black bg-opacity-30 hover:bg-opacity-50 text-white w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300 z-10 ring-1 ring-white/0 group-hover:ring-white/50 hover:ring-white/75 focus:ring-white/75"
          aria-label="Next slide"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5 sm:w-6 sm:h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        </button>
        <div className="absolute bottom-8 sm:bottom-10 left-1/2 transform -translate-x-1/2 flex space-x-3 z-10">
          {slidesData.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goToSlide(idx)}
              className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all duration-300 outline-none ${currentIndex === idx ? 'bg-white scale-110' : 'bg-white bg-opacity-40 hover:bg-opacity-60'}`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      </section>
    </main>
  );
};

export default HomePage;
